/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable eqeqeq */
import React, { Component } from 'react';
import redirectURL from '../redirectURL';
import { getHyphenDDMMMYYYY } from "../common/utils";
import { isIndependentRow } from "./epodDealerFlow";

// EPOD FINAL DEALER UI - "Review POD" slider (section 1-6, 27-30 of the task).
//
// Single Dealer/CUSTOMER entry point replacing the previous separate Clean/Shortage/Damage grid
// buttons. Opens as a right-side sliding panel; the dealer picks exactly one condition and only
// that condition's fields render. This component owns NO business logic of its own - it calls the
// SAME existing /billing/dealerConfirmClean|Shortage|Damage endpoints, with the SAME payload
// shapes, that epodScreen.js/dealerConfirmationScreen.js already used before this consolidation.
// The backend remains the sole authority on eligibility/validity (section 4) - every check here is
// for usability only and is re-verified server-side on submit.
//
// props:
//   show    - boolean, whether a row is currently open (component is only rendered by the parent
//             while a row is selected, so `show` really just gates the enter-transition timing)
//   row     - the queue row (from dealerConditionQueueMap) - carries gc_waybill_no,
//             delivery_datetime/delivered_date, epod_gate_status, shortage_available,
//             damage_available (both computed server-side by listQueue), and
//             dealer_confirmation_rejection_pending
//   options - dealerConditionOptions state (shortage_sla_hours, damage_sla_days,
//             max_remarks_length, damage_images:{min_count,max_count})
//   onClose      - () => void, called on Cancel/backdrop click with no backend call
//   onSubmitted  - ({success, message}) => void, called after any submit attempt so the parent can
//                  show its existing toast and reload the queue - exactly what the old modals did
class ReviewPodSlider extends Component {
    state = {
        entered: false,
        condition: null,
        remarks: '',
        ndvAmount: '',
        shortageQuantity: '',
        damageFiles: [],
        submitting: false,
        error: '',
    };

    componentDidMount() {
        // Mount with the panel off-screen, then flip on the next frame so the CSS transition
        // actually animates the slide-in instead of snapping straight to the open position.
        requestAnimationFrame(() => { this.setState({ entered: true }); });
    }

    // Selecting a different condition must never carry a previous condition's field values into a
    // submission (section 28 - "must not accidentally submit stale values from another condition").
    selectCondition = (condition) => {
        this.setState({
            condition: condition,
            remarks: '', ndvAmount: '', shortageQuantity: '', damageFiles: [],
            error: '',
        });
    }

    onChangeRemarks = (e) => {
        var v = e.target.value;
        var maxLen = this.props.options.max_remarks_length || 500;
        if (v.length > maxLen) v = v.substring(0, maxLen);
        this.setState({ remarks: v, error: '' });
    }
    onChangeNdvAmount = (e) => { this.setState({ ndvAmount: e.target.value, error: '' }); }
    onChangeShortageQuantity = (e) => { this.setState({ shortageQuantity: e.target.value, error: '' }); }
    onChangeDamageFiles = (e) => { this.setState({ damageFiles: Array.from(e.target.files || []), error: '' }); }

    submitClean = () => {
        if (this.state.submitting) return;
        var row = this.props.row;
        this.setState({ submitting: true, error: '' });
        redirectURL.post("/billing/dealerConfirmClean", { rowid: row._id }, { headers: { 'Authorization': localStorage.getItem("userid") } })
            .then((resp) => {
                var ok = resp.data && resp.data.status === "success";
                if (ok) {
                    this.setState({ submitting: false });
                    this.props.onSubmitted({ success: true, message: resp.data.message || 'Marked All Good.' });
                } else {
                    // Server-side failures also close the slider and are shown by the parent's alert;
                    // only client-side validation errors keep it open.
                    this.props.onSubmitted({ success: false, message: (resp.data && resp.data.message) || 'Failed to submit.' });
                }
            })
            .catch((err) => {
                this.props.onSubmitted({ success: false, message: (err.response && err.response.data && err.response.data.message) || 'Failed to submit.' });
            });
    }

    submitShortage = () => {
        var { remarks, ndvAmount, shortageQuantity } = this.state;
        var trimmed = (remarks || '').trim();
        if (!trimmed) { this.setState({ error: 'Remarks are required.' }); return; }
        var ndvNum = Number(String(ndvAmount == null ? '' : ndvAmount).trim());
        if (String(ndvAmount).trim() === '' || isNaN(ndvNum) || !isFinite(ndvNum) || ndvNum < 0) {
            this.setState({ error: 'A valid, non-negative NDV amount is required.' });
            return;
        }
        var qtyNum = Number(String(shortageQuantity == null ? '' : shortageQuantity).trim());
        if (String(shortageQuantity).trim() === '' || isNaN(qtyNum) || !isFinite(qtyNum) || !Number.isInteger(qtyNum) || qtyNum <= 0) {
            this.setState({ error: 'A valid Shortage Quantity (whole number greater than 0) is required.' });
            return;
        }
        if (this.state.submitting) return;
        this.setState({ submitting: true, error: '' });
        redirectURL.post("/billing/dealerConfirmShortage",
            { rowid: this.props.row._id, remarks: trimmed, ndv_amount: ndvNum, shortage_quantity: qtyNum },
            { headers: { 'Authorization': localStorage.getItem("userid") } })
            .then((resp) => {
                var ok = resp.data && (resp.data.status === "success" || resp.data.status === "pending_al_review" || resp.data.status.includes("NDV approved. Classified for Credit Note."));
                if (ok) { this.setState({ submitting: false }); this.props.onSubmitted({ success: true, message: resp.data.message }); }
                else { this.props.onSubmitted({ success: false, message: (resp.data && resp.data.message) || 'Submission failed.' }); }
            })
            .catch((err) => {
                this.props.onSubmitted({ success: false, message: (err.response && err.response.data && err.response.data.message) || 'Submission failed.' });
            });
    }

    submitDamage = () => {
        var { remarks, ndvAmount, damageFiles } = this.state;
        var damageImagesCfg = this.props.options.damage_images || {};
        var minCount = damageImagesCfg.min_count || 1;
        if (damageFiles.length < minCount) {
            this.setState({ error: 'At least ' + minCount + ' damage image(s) are required.' });
            return;
        }
        var ndvNum = Number(String(ndvAmount == null ? '' : ndvAmount).trim());
        if (String(ndvAmount).trim() === '' || isNaN(ndvNum) || !isFinite(ndvNum) || ndvNum < 0) {
            this.setState({ error: 'A valid, non-negative NDV amount is required.' });
            return;
        }
        if (this.state.submitting) return;
        this.setState({ submitting: true, error: '' });
        var formData = new FormData();
        formData.append('rowid', this.props.row._id);
        formData.append('remarks', remarks || '');
        formData.append('ndv_amount', ndvNum);
        damageFiles.forEach((f) => formData.append('damageImages', f));
        redirectURL.post("/billing/dealerConfirmDamage", formData, {
            headers: { 'content-type': 'multipart/form-data', 'Authorization': localStorage.getItem("userid") }
        })
            .then((resp) => {
                var ok = resp.data && resp.data.status === "success";
                if (ok) { this.setState({ submitting: false }); this.props.onSubmitted({ success: true, message: resp.data.message }); }
                else { this.props.onSubmitted({ success: false, message: (resp.data && resp.data.message) || 'Submission failed.' }); }
            })
            .catch((err) => {
                this.props.onSubmitted({ success: false, message: (err.response && err.response.data && err.response.data.message) || 'Submission failed.' });
            });
    }

    formatDate = (row) => {
        var raw = row.delivery_datetime || row.delivered_date;
        if (!raw) return '-';
        try { return getHyphenDDMMMYYYY(raw); } catch (e) { return '-'; }
    }

    render() {
        var { row, options } = this.props;
        if (!row) return null;
        var { entered, condition, remarks, ndvAmount, shortageQuantity, damageFiles, submitting, error } = this.state;
        var shortageAvailable = row.shortage_available !== false; // fail-open only if the backend didn't send the flag at all (older cache) - real block still happens server-side on submit
        var damageAvailable = row.damage_available !== false;
        var maxRemarksLength = options.max_remarks_length || 500;

        return (
            <div>
                {/* Backdrop */}
                <div onClick={this.props.onClose} style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1040,
                    background: 'rgba(0,0,0,0.5)', opacity: entered ? 1 : 0, transition: 'opacity 0.25s ease',
                }} />
                {/* Panel */}
                <div style={{
                    position: 'fixed', top: 0, right: 0, bottom: 0, width: '420px', maxWidth: '95vw',
                    background: '#fff', zIndex: 1050, boxShadow: '-2px 0 12px rgba(0,0,0,0.25)',
                    transform: entered ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.28s ease',
                    display: 'flex', flexDirection: 'column',
                }}>
                    <div style={{ padding: '14px 18px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h5 style={{ margin: 0 }}>Review POD</h5>
                        <button className="btn btn-sm btn-outline-secondary" onClick={this.props.onClose}>Close</button>
                    </div>

                    <div style={{ padding: '16px 18px', overflowY: 'auto', flex: 1 }}>
                        <div style={{ marginBottom: 14, fontSize: 13, color: '#333' }}>
                            <div><b>LR Number:</b> {row.gc_waybill_no}</div>
                            <div><b>Delivery Date:</b> {this.formatDate(row)}</div>
                            <div><b>Current Status:</b> {isIndependentRow(row) ? 'Awaiting Your Confirmation' : (row.epod_gate_status || '-')}</div>
                            {row.dealer_confirmation_rejection_pending ?
                                <div className="badge badge-danger" style={{ marginTop: 6 }}>Rejected - Resubmission Required</div>
                                : null}
                        </div>

                        <div className="form-group">
                            <label><b>Select Condition</b></label>
                            <div>
                                <label style={{ marginRight: 16 }}>
                                    <input type="radio" name="reviewPodCondition" checked={condition === 'clean'}
                                        onChange={() => this.selectCondition('clean')} /> Clean
                                </label>
                                <label style={{ marginRight: 16, opacity: shortageAvailable ? 1 : 0.5 }}>
                                    <input type="radio" name="reviewPodCondition" checked={condition === 'shortage'} disabled={!shortageAvailable}
                                        onChange={() => this.selectCondition('shortage')} /> Shortage
                                </label>
                                <label style={{ opacity: damageAvailable ? 1 : 0.5 }}>
                                    <input type="radio" name="reviewPodCondition" checked={condition === 'damage'} disabled={!damageAvailable}
                                        onChange={() => this.selectCondition('damage')} /> Damage
                                </label>
                            </div>
                            {!shortageAvailable ?
                                <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                                    Shortage window expired ({options.shortage_window_text || ((options.shortage_sla_hours || 48) + ' hours')} from delivery has passed).
                                </div>
                                : null}
                            {!damageAvailable ?
                                <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                                    Damage window expired ({options.damage_window_text || ((options.damage_sla_days || 7) + ' days')} from delivery has passed).
                                </div>
                                : null}
                        </div>

                        {condition === 'clean' ?
                            <div>
                                <p>Are you sure you want to mark LR <b>{row.gc_waybill_no}</b> as Clean?</p>
                            </div>
                            : null}

                        {condition === 'shortage' ?
                            <div>
                                <div className="form-group">
                                    <label>Shortage Quantity *</label>
                                    <input type="number" className="form-control" min="1" step="1"
                                        value={shortageQuantity} onChange={this.onChangeShortageQuantity} />
                                </div>
                                <div className="form-group">
                                    <label>NDV Amount (Rs.) *</label>
                                    <input type="number" className="form-control" min="0" step="0.01"
                                        value={ndvAmount} onChange={this.onChangeNdvAmount} />
                                </div>
                                <div className="form-group">
                                    <label>Remarks * <span style={{ float: 'right', fontSize: 11, color: '#888' }}>{remarks.length}/{maxRemarksLength}</span></label>
                                    <textarea className="form-control" rows="3" maxLength={maxRemarksLength}
                                        value={remarks} onChange={this.onChangeRemarks} />
                                </div>
                            </div>
                            : null}

                        {condition === 'damage' ?
                            <div>
                                <div className="form-group">
                                    <label>Damage Documents / Images *</label>
                                    <input type="file" className="form-control" multiple accept="image/jpeg,image/png,image/webp"
                                        onChange={this.onChangeDamageFiles} />
                                    {damageFiles.length > 0 ?
                                        <div style={{ fontSize: 12, marginTop: 4 }}>{damageFiles.map((f) => f.name).join(', ')}</div>
                                        : null}
                                </div>
                                <div className="form-group">
                                    <label>NDV Amount (Rs.) *</label>
                                    <input type="number" className="form-control" min="0" step="0.01"
                                        value={ndvAmount} onChange={this.onChangeNdvAmount} />
                                </div>
                                <div className="form-group">
                                    <label>Remarks</label>
                                    <textarea className="form-control" rows="3" maxLength={maxRemarksLength}
                                        value={remarks} onChange={this.onChangeRemarks} />
                                </div>
                            </div>
                            : null}

                        {error ? <div className="alert alert-danger" style={{ marginTop: 10 }}>{error}</div> : null}
                    </div>

                    <div style={{ padding: '12px 18px', borderTop: '1px solid #ddd', textAlign: 'right' }}>
                        <button className="btn btn-secondary" style={{ marginRight: 8 }} onClick={this.props.onClose} disabled={submitting}>Cancel</button>
                        {condition === 'clean' ?
                            <button className="btn btn-success" onClick={this.submitClean} disabled={submitting}>Confirm Clean</button>
                            : null}
                        {condition === 'shortage' ?
                            <button className="btn btn-warning" onClick={this.submitShortage} disabled={submitting || !shortageAvailable}>Submit Shortage</button>
                            : null}
                        {condition === 'damage' ?
                            <button className="btn btn-danger" onClick={this.submitDamage} disabled={submitting || !damageAvailable}>Submit Damage</button>
                            : null}
                    </div>
                </div>
            </div>
        );
    }
}

export default ReviewPodSlider;
